import React, { useState, useEffect, useRef } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import FocusGrow from "../FocusGrow"
import parse from "html-react-parser"
import PubSub from "pubsub-js"

const sizeToClasses = {
  big: "h-[385px] md:h-[522px]",
  medium: "h-[255px] md:h-[338px]",
  small: "h-[194px] md:h-[256px]",
  tiny: "h-[115px] md:h-[154px]",
}

const positions = {
  right: "absolute top-6 right-7",
  center: "absolute top-12 left-1/2 transform -translate-x-1/2",
  big_center: "absolute top-20 left-1/2 transform -translate-x-1/2",
}

const Card = ({ card, idx, isSelected, onSelect }) => {
  const cardRef = useRef(null)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (!isMobile) return
    const checkPosition = () => {
      if (!cardRef.current) return
      const rect = cardRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const cardCenterY = rect.top + rect.height / 2
      const screenCenterY = windowHeight / 2
      const distanceFromCenter =
        Math.abs(cardCenterY - screenCenterY) / windowHeight
      if (distanceFromCenter < 0.2) {
        onSelect(idx)
      }
    }
    checkPosition()
    window.addEventListener("scroll", checkPosition)
    return () => window.removeEventListener("scroll", checkPosition)
  }, [isMobile, idx, onSelect])

  const [size] = Array.isArray(card.size) ? card.size : [card.size]
  const [icon_location] = Array.isArray(card.icon_location)
    ? card.icon_location
    : [card.icon_location] || ["center"]

  const heightClass = sizeToClasses[size] || sizeToClasses.small
  const imageSize = "104px"
  let positionClass = positions[icon_location] || positions.center

  if (size === "big") {
    positionClass = positions.big_center
  }

  // Handle tiny cards differently
  if (size === "tiny") {
    return renderTinyCard(card, idx)
  }

  return (
    <div
      ref={cardRef}
      key={idx}
      className={`relative w-full ${heightClass} flex flex-col justify-between bg-theme_white px-5 md:px-10 pb-2 md:pb-6 pt-8 rounded-[24px] overflow-hidden shadow-custom_card`}
      onMouseEnter={() => !isMobile && onSelect(idx)}
      onMouseLeave={() => !isMobile && onSelect(null)}
    >
      <FocusGrow
        radiusMultiplier={1.3}
        growClass="bg-primary_lt z-0"
        className={positionClass}
        isExpandedExternal={isSelected}
      >
        <img
          src={card.icon}
          alt={card.title}
          className={`relative z-10 h-auto object-cover mx-auto`}
          width={imageSize}
        />
      </FocusGrow>

      <div className="relative flex-1 flex flex-col justify-end z-10">
        <Typography
          variant="body_24_accent"
          className="!text-[18px] lg:!text-[20px] xl:!text-[24px] text-text my-0"
        >
          {card.title}
        </Typography>

        <Typography variant="info_16" className="text-text">
          {card.description ? parse(card.description.replace(/\n/g, "<br />")) : ''}
        </Typography>
      </div>
    </div>
  )
}

const renderTinyCard = (card, idx) => {
  const heightClass = sizeToClasses.tiny
  return (
    <Button
      key={idx}
      onClick={() => PubSub.publish("open-contacts-partners")}
      variant="card_button"
      className={heightClass}
    >
      <Typography
        variant="subtitle_32_accent"
        className="text-primary text-left !text-[24px] md:!text-[26px] lg:!text-[28px] xl:!text-[32px] group-hover:text-white p-0 m-0"
      >
        {card.title}
      </Typography>

      <Typography
        variant="info_16"
        className="text-text text-left group-hover:text-white m-0"
      >
        {parse(card.description.replace(/\n/g, "<br />"))}
      </Typography>
    </Button>
  )
}

const Column = ({ children }) => (
  <div className="flex flex-col gap-8 md:gap-12 w-full">{children}</div>
)

const distributeCardsToColumns = (cards, gridCols) => {
  const columns = Array.from({ length: gridCols }, () => [])
  if (!Array.isArray(cards)) return []
  cards.forEach((card, idx) => {
    columns[idx % gridCols].push(card)
  })
  return columns
}

function WhyChooseAqua({ grid_cols = 2 }) {
  const [selectedCardIndex, setSelectedCardIndex] = useState(null)

  return (
    <CMSRender
      contentType="AquaSiteCardGroup"
      match={{ "content.id": "why_choose_aqua" }}
      renderItem={(section) => {
        const columns = distributeCardsToColumns(section.cards, grid_cols)
        let globalIndex = 0

        return (
          <div className="w-full px-4 md:px-[56px] lg:px-[56px] xl:px-[76px] mb-16 md:mb-20 lg:mb-40">
            <div className="max-w-[500px] md:max-w-[800px] lg:max-w-full mx-auto">
              <Typography
                component="h2"
                variant="h1"
                className="text-center my-6 md:my-12"
              >
                {section.title}
              </Typography>

              <Typography
                variant="subtitle_32"
                className="text-center mb-10 md:mb-[80px]"
              >
                {section.description}
              </Typography>
            </div>

            <div className="w-full max-w-[464px] md:max-w-[800px] lg:max-w-[900px] xl:max-w-[976px] mx-auto">
              <div className="flex flex-col md:flex-row gap-8 md:gap-12">
                {columns.map((columnCards, colIdx) => (
                  <Column key={colIdx}>
                    {columnCards.map((card, idx) => {
                      const currentGlobalIndex = globalIndex++
                      return (
                        <Card
                          key={currentGlobalIndex}
                          card={card}
                          idx={currentGlobalIndex}
                          isSelected={selectedCardIndex === currentGlobalIndex}
                          onSelect={setSelectedCardIndex}
                        />
                      )
                    })}
                  </Column>
                ))}
              </div>
            </div>
          </div>
        )
      }}
    />
  )
}

const AldooComponent = {
  id: 'WhyChooseAqua',
  component: WhyChooseAqua,
  input: {},
  meta: {
    name: 'Why Choose Aqua',
    description: 'Interactive card grid with expandable cards showing benefits',
    category: 'Marketing',
  },
}

export { AldooComponent }
export default WhyChooseAqua
